/**
 * Admin Worker API クライアント。
 *
 * 全リクエストに LIFF IDToken を Authorization: Bearer で付与する。
 * Worker 側で OWNER_LINE_USER_ID と一致するか検証される（403 を返す）。
 *
 * WORKER_BASE は本番 URL を直書き。公開 URL なので機密ではない。
 * `window.__RESERVATION_WORKER_BASE__` で上書き可能（ローカル動作確認用）。
 */

import type {
  CancelResult,
  CancelTemplateKey,
  Filter,
  ListResult,
  MeResult,
  Reservation,
  UpdateStatusResult,
} from "./types";

const WORKER_BASE: string =
  (window as any).__RESERVATION_WORKER_BASE__ ??
  "https://empath-line-reservation-worker.sukechii-0623.workers.dev";

// Phase 1.5.5: マルチテナント化対応。
// 当面 Empath LINE 単独運用のため hard-coded。
// 他テナント展開時は URL クエリパラメータ ?account=acc_xxx 等から動的に取得する。
const ACCOUNT_ID = "acc_empath01";

function getIdToken(): string {
  const tok = (window as any).liff?.getIDToken();
  if (!tok) throw new Error("LIFF Token 取得失敗");
  return tok;
}

function authHeaders(): Record<string, string> {
  return {
    "Authorization": `Bearer ${getIdToken()}`,
    "Content-Type": "application/json",
    "X-Account-Id": ACCOUNT_ID,
  };
}

/**
 * API エラーを画面側で扱いやすくするために status を保持した Error を投げる。
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* レスポンスが JSON でないケース */
  }
  if (!res.ok) {
    const msg = (body && (body.message || body.error)) || `HTTP ${res.status}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

export async function checkOwner(): Promise<MeResult> {
  const res = await fetch(`${WORKER_BASE}/api/admin/me`, {
    headers: authHeaders(),
  });
  return parseOrThrow<MeResult>(res);
}

export async function listReservations(filter: Filter): Promise<ListResult> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/reservations?filter=${encodeURIComponent(filter)}`,
    { headers: authHeaders() }
  );
  return parseOrThrow<ListResult>(res);
}

export async function getReservation(id: string): Promise<Reservation> {
  const res = await fetch(`${WORKER_BASE}/api/admin/reservations/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return parseOrThrow<Reservation>(res);
}

export async function updateMemo(id: string, memo: string): Promise<{ ok: true }> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/reservations/${encodeURIComponent(id)}/memo`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ memo }),
    }
  );
  return parseOrThrow<{ ok: true }>(res);
}

export async function updateStatus(
  id: string,
  status: "completed" | "no_show"
): Promise<UpdateStatusResult> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/reservations/${encodeURIComponent(id)}/status`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ status }),
    }
  );
  return parseOrThrow<UpdateStatusResult>(res);
}

export async function cancelReservation(
  id: string,
  templateKey: CancelTemplateKey,
  internalNote: string
): Promise<CancelResult> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/reservations/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        template_key: templateKey,
        internal_note: internalNote,
      }),
    }
  );
  return parseOrThrow<CancelResult>(res);
}

// ===== Phase 1.5.4 営業日設定 API =====

export interface BusinessHoursDto {
  start: string;
  end: string;
  slot_minutes: number;
}

export interface HolidayDto {
  date: string;
  kind: "national" | "custom" | "temporary";
  label: string | null;
}

export interface BusinessSettingsDto {
  weekday_hours: BusinessHoursDto;
  weekend_hours: BusinessHoursDto;
  holidays: HolidayDto[];
  is_default: boolean;
}

export async function getBusinessSettings(): Promise<BusinessSettingsDto> {
  const res = await fetch(`${WORKER_BASE}/api/admin/business-settings`, {
    headers: authHeaders(),
  });
  return parseOrThrow<BusinessSettingsDto>(res);
}

export async function updateBusinessHours(
  dayType: "weekday" | "weekend",
  hours: { start_time: string; end_time: string; slot_minutes: number }
): Promise<{ ok: boolean; day_type: string }> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/business-settings/hours/${dayType}`,
    {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(hours),
    }
  );
  return parseOrThrow(res);
}

export async function upsertHolidays(
  items: Array<{ date: string; kind: HolidayDto["kind"]; label?: string | null }>
): Promise<{ ok: boolean; upserted: number }> {
  const res = await fetch(`${WORKER_BASE}/api/admin/business-settings/holidays`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ items }),
  });
  return parseOrThrow(res);
}

export async function deleteHoliday(
  date: string
): Promise<{ ok: boolean; date: string }> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/business-settings/holidays/${encodeURIComponent(date)}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
  return parseOrThrow(res);
}

export async function seedNationalHolidays(): Promise<{ ok: boolean; seeded: number }> {
  const res = await fetch(
    `${WORKER_BASE}/api/admin/business-settings/seed-national-holidays`,
    {
      method: "POST",
      headers: authHeaders(),
    }
  );
  return parseOrThrow(res);
}
